import {
    computed,
    effect,
    inject,
    Injectable,
    Signal,
    signal,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { IqEventBusBrokerService } from '@dat/iq-event-bus';
import { Control, Option } from '../../models';
import {
    DashboardControlProps,
    DashControlController,
} from './iq-controls-status.models';

import { InputValueTypes, Validators } from './iq-controls-action.models';
import { Tab } from '@dat/iq-control-tab';
import { set } from 'lodash';

@Injectable({
    providedIn: 'root',
})
export class IqControlsStatusService {
    protected readonly _formGroups = signal<Record<string, FormGroup>>({});
    protected readonly _controlProps = signal<DashboardControlProps>({});
    private formCache: Map<string, any> = new Map<string, any>();
    private _events: IqEventBusBrokerService = inject(IqEventBusBrokerService);

    constructor() {
        effect(() => {
        //     // // ! DEBUG CODE: DON'T DELETE!
        //     console.log('Current Control Props', this._controlProps());
        //     console.log('Current FormGroups', this._formGroups());
        //     // // ! END OF DEBUG CODE
        });
    }

    private _updateControl(
        dashboardControlId: string,
        controller: DashControlController,
        newControl: Control,
    ) {
        this._controlProps.set({
            ...this._controlProps(),
            [dashboardControlId]: controller.replaceControl(
                newControl as Control,
            ),
        });
    }

    hasFg(id: string) {
        return !!this._formGroups()[id];
    }
    hasControls(id: string) {
        return this._controlProps()[id]?.getControls()?.length > 0;
    }
    getFg(id: string): FormGroup<any> {
        return this._formGroups()[id];
    }

    //KEEP
    setControlProps(
        dashboardControlId: string,
        controlName: string,
        props: Record<string, any>,
    ) {
        const controller: DashControlController =
            this._controlProps()[dashboardControlId];

        if (!controller) {
            console.warn(
                'Dashboard Control at Id:',
                dashboardControlId,
                'does not exist.',
            );
            return;
        }
        const oldControl: Control = controller?.getControlByName(controlName);
        if (!oldControl) {
            console.warn(
                'Dashboard Control at Id:',
                dashboardControlId,
                'does not have a control with the name ',
                controlName,
            );
            return;
        }

        const newControl = { ...oldControl, ...props };

        this._updateControl(dashboardControlId, controller, newControl);
    }

    updateTabs(dashboardControlId: string, controlName: string, tabs: Tab[], value?: Tab) {
        const controller: DashControlController =
            this._controlProps()[dashboardControlId];

        if (!controller) {
            console.warn(
                'Dashboard Control at Id:',
                dashboardControlId,
                'does not exist.',
            );
            return;
        }

        const oldControl: Control = controller?.getControlByName(controlName);

        if (!oldControl && oldControl?.type !== 'Tabs') {
            console.warn(
                'Dashboard Control at Id:',
                dashboardControlId,
                'does not have a control with the name ',
                controlName,
            );
            return;
        }

        const fg = this._formGroups()[dashboardControlId];
        const newControl: any = {
            ...oldControl,
            type: 'Tabs',
            tabs: tabs,
        };
        if (value) {
            const foundValue = tabs.find((tab) => tab.value === value.value);
            fg.get(controlName).setValue(foundValue);
            newControl.value = foundValue;
        }
        this._updateControl(dashboardControlId, controller, newControl);
       
        fg.updateValueAndValidity();
    }

    updateControlLabel(
        dashboardControlId: string,
        controlName: string,
        label: string,
        checkedLabel?: string,
        uncheckedLabel?: string,
    ) {
        const controller: DashControlController =
            this._controlProps()[dashboardControlId];

        if (!controller) {
            console.warn(
                'Dashboard Control at Id:',
                dashboardControlId,
                'does not exist.',
            );
            return;
        }

        const oldControl: Control = controller?.getControlByName(controlName);

        if (!oldControl) {
            console.warn(
                'Dashboard Control at Id:',
                dashboardControlId,
                'does not have a control with the name ',
                controlName,
            );
            return;
        }

        const fg = this._formGroups()[dashboardControlId];
        const newControl: any = {
            ...oldControl,
        };

        if (label) set(newControl, 'label', label);
        if (checkedLabel) set(newControl, 'checkedLabel', checkedLabel);
        if (uncheckedLabel) set(newControl, 'uncheckedLabel', uncheckedLabel);

        this._updateControl(dashboardControlId, controller, newControl);

        fg.updateValueAndValidity();
    }

    setControlValue(
        dashboardControlId: string,
        controlName: string,
        value: InputValueTypes,
    ) {
        const controller: DashControlController =
            this._controlProps()[dashboardControlId];

        if (!controller) {
            console.warn(
                'Dashboard Control at Id:',
                dashboardControlId,
                'does not exist.',
            );
            return;
        }

        const oldControl: Control = controller?.getControlByName(controlName);

        if (!oldControl) {
            console.warn(
                'Dashboard Control at Id:',
                dashboardControlId,
                'does not have a control with the name ',
                controlName,
            );
            return;
        }

        const fg = this._formGroups()[dashboardControlId];
        const newControl: Control = {
            ...oldControl,
            value: value as any,
        };

        this._updateControl(dashboardControlId, controller, newControl);

        fg.get(controlName).setValue(value);
        fg.updateValueAndValidity();
    }

    setControlEnablement(
        dashboardControlId: string,
        controlName: string,
        enabled: boolean,
    ): void {
        const controller: DashControlController =
            this._controlProps()[dashboardControlId];

        if (!controller) {
            console.warn(
                'Dashboard Control at Id:',
                dashboardControlId,
                'does not exist.',
            );
            return;
        }

        const oldControl: Control = controller?.getControlByName(controlName);

        if (!oldControl) {
            console.warn(
                'Dashboard Control at Id:',
                dashboardControlId,
                'does not have a control with the name ',
                controlName,
            );
            return;
        }

        const fg = this._formGroups()[dashboardControlId];
        const newControl: Control = {
            ...oldControl,
            disabled: !enabled,
        };

        this._updateControl(dashboardControlId, controller, newControl);
        enabled ? fg.get(controlName).enable() : fg.get(controlName).disable();
        fg.updateValueAndValidity();
    }

    /**
     * Adds the formGroup from the dashboard controls to the service
     * @param id - the dashboard controls id
     * @param formGroup the form group for value, disable, and validation
     */
    registerFormGroup(id: string, formGroup: FormGroup): void {
        this._formGroups.set({
            ...this._formGroups(),
            [id]: formGroup,
        });

        this.formCache.set(id, {});
    }

    getFormCache(formId: string) {
        if (this.formCache.has(formId)) {
            return this.formCache.get(formId);
        }
        return {};
    }

    updateFormCache(formId: string, data: any) {
        const oldValue = this.formCache.get(formId);

        this.formCache.set(formId, { ...oldValue, ...data });
    }

    resetFormGroup(id: string): void {
        const formGroup = this._getFormGroup(id);
        formGroup.reset();
    }

    disableFormGroup(id: string, disabled: boolean): void {
        const formGroup = this._getFormGroup(id);
        disabled ? formGroup.disable() : formGroup.enable();
    }

    disableControl(
        dashboardControlId: string,
        controlId: string,
        disabled: boolean,
    ): void {
        this._controlProps()[dashboardControlId].disableControl(
            controlId,
            disabled,
        );
    }

    disableOption(
        dashboardControlId: string,
        controlId: string,
        optionId: string,
        disabled: boolean,
    ): void {
        const controller = this._controlProps()[
            dashboardControlId
        ].disableOption(controlId, optionId, disabled);
    }

    getControls(id: string): Signal<Control[]> {
        return computed(() => {
            return this._controlProps()?.[id]?.getControls() ?? [];
        });
    }

    addControl(
        dashboardControlId: string,
        control: Control,
        indexAt: number | undefined | null,
    ): void {
        const controller: DashControlController =
            this._controlProps()[dashboardControlId];

        if (!controller) {
            console.warn(
                'Dashboard Control at Id:',
                dashboardControlId,
                'does not exist.',
            );
            return;
        }
        const controlExists = controller.getControlByName(control.name);
        if (!!controlExists) {
            console.warn(
                'Control with name:',
                control.name,
                'already exists in the control dashboard',
            );
            return;
        }

        const newControlProps = controller.addControl(control, indexAt);

        this._controlProps.set({
            ...this._controlProps(),
            [dashboardControlId]: newControlProps,
        });
    }

    addAllControls(id: string, controls: Control[]): void {
        const newControlProps: DashControlController = !!this._controlProps()?.[
            id
        ]
            ? this._controlProps()[id].addAll(controls)
            : new DashControlController().addAll(controls);

        this._controlProps.set({
            ...this._controlProps(),
            [id]: newControlProps,
        });
    }

    removeControl(dashboardControlId: string, controlNames: string[]): void {
        const controller = this._controlProps()[dashboardControlId];

        if (!controller) {
            console.warn(
                'Dashboard Control at Id:',
                dashboardControlId,
                'does not exist.',
            );
            return;
        }

        const newControlProps = controller.removeControls(controlNames);

        this._controlProps.set({
            ...this._controlProps(),
            [dashboardControlId]: newControlProps,
        });
    }

    setDashboardControlsValue(
        dashboardControlId: string,
        values: Record<string, InputValueTypes>,
    ): void {
        try {
            const controls: Control[] = this.getControls(dashboardControlId)();
            console.log(controls)
            console.log(values)
            const changedValues: Record<string, InputValueTypes> = Object.keys(
                values,
            ).reduce((acc: Record<string, InputValueTypes>, name: string) => {
                const control: Control = controls.find(
                    (ctrl) => ctrl.name === name,
                );
                console.log(acc, name)
                console.log(controls)
                if (!control) return acc;
                console.log({control})
                const modifiedValue =
                    control.type === 'Tabs'
                        ? control.tabs.find(
                              (tab) =>
                                  tab.value === (values[name] as Tab).value,
                          )
                        : values[name];
                console.log({modifiedValue})
                return {
                    ...acc,
                    [name]: modifiedValue,
                };
            }, {});

            this._formGroups()[dashboardControlId].setValue(changedValues);
            this._controlProps()[dashboardControlId].setDashboardControls(
                changedValues,
            );
        } catch (e) {
            console.warn(e);
        }
    }

    patchDashboardControlsValue(
        dashboardControlId: string,
        values: Record<string, InputValueTypes>,
    ) {
        try {
            const controls: Control[] = this.getControls(dashboardControlId)();
            const changedValues: Record<string, InputValueTypes> = Object.keys(
                values,
            ).reduce((acc: Record<string, InputValueTypes>, name: string) => {
                const control: Control = controls.find(
                    (ctrl) => ctrl.name === name,
                );

                if (!control) return acc;

                const modifiedValue =
                    control.type === 'Tabs'
                        ? control.tabs.find(
                              (tab) =>
                                  tab.value === (values[name] as Tab).value,
                          )
                        : values[name];

                return {
                    ...acc,
                    [name]: modifiedValue,
                };
            }, {});

            this._formGroups()[dashboardControlId].patchValue(changedValues);

            Object.keys(changedValues).forEach((name) => {
                this._formGroups()
                    [dashboardControlId].get(name)
                    .setValue(changedValues[name]);
            });

            this._controlProps.set({
                ...this._controlProps(),
                [dashboardControlId]:
                    this._controlProps()[
                        dashboardControlId
                    ].patchDashboardControls(changedValues),
            });
            this._formGroups()[dashboardControlId].updateValueAndValidity();
        } catch (e) {
            console.warn(e);
        }
    }

    patchDashboardControls(
        dashboardControlId: string,
        value: Record<string, any>,
    ): void {
        this._formGroups()[dashboardControlId].patchValue(value);
        this._controlProps()[dashboardControlId].patchDashboardControls(value);
    }

    setControl(
        dashboardControlId: string,
        controlId: string,
        control: Control,
    ): void {
        // TODO: Need to update FormGroup??
        // const oldControl: Control = this._formGroups()[dashboardControlId].get(controlId)
        this._controlProps()[dashboardControlId].setControl(controlId, control);
    }

    setValidators(
        dashboardControlId: string,
        controlId: string,
        validators: Validators,
    ): void {
        const controls: Control[] = this.getControls(dashboardControlId)();
        const updatedControls: Control[] = controls.map((ctrl) => {
            return ctrl.id === controlId ? { ...ctrl, ...validators } : ctrl;
        });
        this.replaceControls(dashboardControlId, updatedControls);
    }

    // TODO: should onSubmit be IDatiqRules<IDatiqAction>??
    updateProps(dashboardControlId: string, controlUpdates: Control[]) {
        // 2. Update the control props
        const controls: Control[] = this.getControls(dashboardControlId)();
        const updatedControls: Control[] = controls.map((oldControl) => {
            const updatedControl = controlUpdates.find((newControl) => {
                newControl.id === oldControl.id;
            });
            return updatedControl ? { ...updatedControl } : { ...oldControl };
        });
        this.replaceControls(dashboardControlId, updatedControls);
    }

    patchControl(
        dashboardControlId: string,
        controlId: string,
        control: Partial<Control>,
    ): void {
        this._controlProps()[dashboardControlId].patchControl(
            controlId,
            control,
        );
    }

    setOption(
        dashboardControlId: string,
        controlId: string,
        optionId: string,
        value: Option | Tab,
    ): void {
        this._controlProps()[dashboardControlId].setOption(
            controlId,
            optionId,
            value,
        );
    }

    patchOption(
        dashboardControlId: string,
        controlId: string,
        optionId: string,
        value: Partial<Option> | Partial<Tab>,
    ): void {
        this._controlProps()[dashboardControlId].patchOption(
            controlId,
            optionId,
            value,
        );
    }

    replaceControls(id: string, controls: Control[]): void {
        const newControlProps: DashControlController = !!this._controlProps()?.[
            id
        ]
            ? this._controlProps()?.[id]?.replaceAll(controls)
            : new DashControlController().replaceAll(controls);

        this._controlProps.set({
            ...this._controlProps(),
            [id]: newControlProps,
        });
    }

    protected _getFormGroup(key: string): FormGroup {
        return this._formGroups()?.[key] ?? new FormGroup({});
    }
}
